import { ApiError } from "@carepass/api-client";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { useAdminSession, useAdminUiStore } from "../../app/providers";
import { parseAdminEntityId, pathForAdminEntityScreen } from "../../app/routes";
import { failure } from "../workspace/behavior";
import type { Schemas } from "../workspace/api.generated";
import type { Command } from "../workspace/model";
import { StateNotice } from "../workspace/shared";
import { RefundView } from "./components/RefundView";
import { useExecuteRefund, useRefundDetail } from "./queries";

function queryState(query: ReturnType<typeof useRefundDetail>) {
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

function unresolvedRefundId(
  commands: Readonly<Record<string, { readonly startedAt: string }>>,
): number | undefined {
  const entry = Object.entries(commands)
    .filter(([key]) => key.startsWith("refund-execute:"))
    .sort(([, left], [, right]) => right.startedAt.localeCompare(left.startedAt))[0];
  const parsed = entry ? Number(entry[0].slice("refund-execute:".length)) : Number.NaN;
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function RefundDetailPageForRefund({ refundId }: { readonly refundId: number }) {
  const session = useAdminSession();
  const commands = useAdminUiStore((state) => state.unresolvedCommands);
  const markCommandUnresolved = useAdminUiStore((state) => state.markCommandUnresolved);
  const resolveCommand = useAdminUiStore((state) => state.resolveCommand);
  const unresolvedId = useMemo(() => unresolvedRefundId(commands), [commands]);
  const detail = useRefundDetail(refundId);
  const execute = useExecuteRefund();
  const navigation = useAdminNavigation({
    contextKey: "refunds",
    defaultListPath: "/refunds?page=0&size=20",
    originLabel: "환불 상세",
  });
  const [notice, setNotice] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const authorities = session.authSession?.authorities ?? [];

  const refresh = async () => {
    setRecoveryError("");
    const result = await detail.refetch();
    if (result.error) throw result.error;
    if (unresolvedId === refundId) resolveCommand(`refund-execute:${refundId}`);
  };

  const reconcile = async () => {
    const result = await detail.refetch();
    if (result.error) throw result.error;
    if (result.data) {
      resolveCommand(`refund-execute:${refundId}`);
      setNotice(`${result.data.refund.refundNo}의 최신 상태는 ${result.data.refund.status}입니다.`);
    }
    return result.data?.refund;
  };

  const runCommand = async (command: Command) => {
    if (command.operation !== "POST /api/v1/admin/refunds/{refundId}/execute") {
      throw new Error(`Unsupported refund command: ${command.operation}`);
    }
    const commandKey = `refund-execute:${refundId}`;
    markCommandUnresolved(commandKey, {
      operation: command.operation,
      targetId: String(refundId),
      startedAt: new Date().toISOString(),
    });
    try {
      return await execute.mutateAsync(refundId);
    } catch (cause) {
      if (!failure(cause).reconcile) resolveCommand(commandKey);
      throw cause;
    }
  };

  const executed = async (updated: Schemas["RefundView"]) => {
    resolveCommand(`refund-execute:${refundId}`);
    setNotice(`${updated.refundNo} 환불 요청 결과가 ${updated.status} 상태로 반영되었습니다.`);
    const result = await detail.refetch();
    if (result.error) {
      setRecoveryError("환불 결과는 확인했지만 상세를 갱신하지 못했습니다. 최신 상태를 다시 조회해 주세요.");
      throw result.error;
    }
  };

  const memberId = detail.data?.memberId;
  const memberHref = memberId && authorities.includes("MEMBER_READ")
    ? pathForAdminEntityScreen("06", memberId)
    : undefined;

  return (
    <RefundView
      mode="detail"
      filters={{ status: "", method: "", memberId: "", from: "", to: "" }}
      selected={detail.data?.refund}
      detail={detail.data}
      paymentBreakdowns={detail.data?.paymentBreakdowns}
      memberId={memberId}
      state={queryState(detail)}
      canCancelPayment={authorities.includes("PAYMENT_CANCEL")}
      canExecuteTransfer={authorities.includes("TRANSFER_EXECUTE")}
      unresolvedRefundId={unresolvedId === refundId ? unresolvedId : undefined}
      notice={notice}
      recoveryError={recoveryError}
      detailHref={(refund) => pathForAdminEntityScreen("23", refund.refundId)}
      backHref={navigation.backPath}
      backLabel={navigation.backLabel}
      onBack={navigation.backToList}
      memberHref={memberHref}
      onOpenMember={memberHref ? () => navigation.openRelated(memberHref, "환불 상세") : undefined}
      onSearch={() => undefined}
      onPageChange={() => undefined}
      onSelect={() => undefined}
      onRetry={() => { void detail.refetch(); }}
      onRefresh={refresh}
      onReconcile={reconcile}
      onCommand={runCommand}
      onExecuted={executed}
    />
  );
}

export function RefundDetailPage() {
  const refundId = parseAdminEntityId(useParams().refundId);
  if (refundId === null) return <StateNotice state="not-found" />;
  return <RefundDetailPageForRefund key={refundId} refundId={refundId} />;
}
